import {
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'

import {TENANT_NO_STORAGE_RESIDENCY_REGION} from 'definitions/tenantConfig'
import type {GetTenantConfigResponse} from 'interfaces'
import {PBFFService} from 'services/api'

/** React Query key for `GET /tenant/config` (PBFF). */
export const TENANT_CONFIG_QUERY_KEY = 'getTenantConfig'

/** Client-only save acknowledgement, kept with the tenant's query cache. */
export interface TenantConfigQueryData extends GetTenantConfigResponse {
  savedResidencyRegion?: string
}

export interface UseTenantConfigQueryOptions {
  /**
   * When false, the query will not execute. Defaults to true.
   * This is useful for callers that may mount before auth is available.
   */
  enabled?: boolean
}

/**
 * Loads tenant config from the portal BFF. Callers decide how to interpret
 * `data` (e.g. residency gating) using `isSuccess` / error state as needed.
 */
export const useTenantConfigQuery = (
  options?: UseTenantConfigQueryOptions,
): UseQueryResult<GetTenantConfigResponse> => {
  const queryClient = useQueryClient()

  return useQuery<TenantConfigQueryData>({
    queryKey: [TENANT_CONFIG_QUERY_KEY],
    queryFn: async () => {
      const config = await PBFFService.getTenantConfig()
      const cached = queryClient.getQueryData<TenantConfigQueryData>([
        TENANT_CONFIG_QUERY_KEY,
      ])

      // A successful save remains authoritative across refetches and routes
      // until this tenant's read endpoint reports storage is enabled.
      if (
        cached?.savedResidencyRegion &&
        cached.data.tenantId === config.data.tenantId &&
        config.data.residencyRegion === TENANT_NO_STORAGE_RESIDENCY_REGION
      ) {
        return {
          ...config,
          data: {...config.data, residencyRegion: cached.savedResidencyRegion},
          savedResidencyRegion: cached.savedResidencyRegion,
        }
      }

      // The raw response drops the marker once acknowledged or tenant-switched.
      return config
    },
    enabled: options?.enabled ?? true,
  })
}
